import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer,
  InputAdornment,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  GetSubScriptionList,
  ISubscription,
} from "../../services/SubscriptionServices";
import { TableBodySubsciptionList } from "../../utils/ContentCardList";
import { useSnackbar } from "notistack";

interface DrawerAssignSubscriptionProps {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
}

const DrawerSubscription: React.FC<DrawerAssignSubscriptionProps> = ({
  open,
  onClose,
  setSelected,
}) => {
  const theme = useTheme();

  const handleSelect = async (id: string): Promise<number> => {
    setSelected(id);
    onClose();
    return 200;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: theme.spacing(2),
          borderBottomLeftRadius: theme.spacing(2),
          width: "30%",
          height: "100vh",
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        component="form"
        aria-label="Video List"
        sx={{
          display: "flex",
          backgroundColor: theme.palette.background.paper,
          flexDirection: "column",
          height: "100vh",
          maxHeight: "100vh",
          boxSizing: "border-box",
        }}
      >
        <Box>
          <SubscriptionList onSelect={handleSelect} />
        </Box>
      </Box>
    </Drawer>
  );
};

export default DrawerSubscription;

const SubscriptionList = ({
  onSelect,
}: {
  onSelect: (id: string) => Promise<number>;
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const rowsPerPageOptions = [5, 15, 25];
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    fetchSubscriptions();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await GetSubScriptionList({ page, size: 10 });
      setSubscriptions(data.content);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.paper,
        flexDirection: "column",
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        padding: theme.spacing(1),
        boxSizing: "border-box",
      }}
    >
      <Box
        mb={2}
        mt={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">
          <b style={{ color: "gray", paddingLeft: 5, cursor: "none" }}>
            {"Choose Subscription"}
          </b>
        </Typography>
        <TextField
          placeholder="Search by name"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginBottom: theme.spacing(1),
          scrollbarWidth: "none",
        }}
      >
        <TableBodySubsciptionList
          drawer
          loading={loading}
          Subscriptions={subscriptions}
          callBackchange={onSelect}
        />
      </Box>
      <Box sx={{ marginTop: "auto" }}>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};
